// src/components/message-board/DrawingCanvas.js
import React, { useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, RotateCcw, Undo, Palette, Eraser, Plus, Trash2, ChevronLeft, ChevronRight, Play, Pause, Film } from 'lucide-react';
import { dynastyTheme } from '../../services/colorService';
import GIF from 'gif.js';

const DrawingCanvas = ({ onSave, onCancel }) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  
  // Drawing state
  const [isDrawing, setIsDrawing] = useState(false);
  const [brushColor, setBrushColor] = useState('#eab308');
  const [brushSize, setBrushSize] = useState(3);
  const [isEraser, setIsEraser] = useState(false);
  const [history, setHistory] = useState([]);
  const [currentStep, setCurrentStep] = useState(-1);
  
  // Animation state
  const [frames, setFrames] = useState([]);
  const [currentFrameIndex, setCurrentFrameIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [fps, setFps] = useState(8);
  const [isAnimationMode, setIsAnimationMode] = useState(false);

  const canvasWidth = 800;
  const canvasHeight = 500;
  const backgroundColor = '#171717';

  // Initialize canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, canvasWidth, canvasHeight);
    
    // Initialize first frame
    const initialFrame = canvas.toDataURL();
    setFrames([initialFrame]);
    saveToHistory();
  }, []);

  // Load current frame when frame index changes
  useEffect(() => {
    if (frames.length > 0 && !isPlaying) {
      loadFrame(currentFrameIndex);
    }
  }, [currentFrameIndex, isPlaying]);

  // Animation playback
  useEffect(() => {
    if (isPlaying && frames.length > 1) {
      animationRef.current = setInterval(() => {
        setCurrentFrameIndex((prev) => (prev + 1) % frames.length);
      }, 1000 / fps);
      
      return () => {
        if (animationRef.current) {
          clearInterval(animationRef.current);
        }
      };
    }
  }, [isPlaying, frames.length, fps]);

  const loadFrame = (index) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      ctx.clearRect(0, 0, canvasWidth, canvasHeight);
      ctx.drawImage(img, 0, 0);
    };
    
    img.src = frames[index];
  };

  const saveCurrentFrame = () => {
    const canvas = canvasRef.current;
    const frameData = canvas.toDataURL();
    const newFrames = [...frames];
    newFrames[currentFrameIndex] = frameData;
    setFrames(newFrames);
  };

  const saveToHistory = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const dataUrl = canvas.toDataURL();
    const newHistory = history.slice(0, currentStep + 1);
    newHistory.push(dataUrl);
    setHistory(newHistory);
    setCurrentStep(newHistory.length - 1);
  };

  const handleUndo = () => {
    if (currentStep <= 0) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      ctx.clearRect(0, 0, canvasWidth, canvasHeight);
      ctx.drawImage(img, 0, 0);
    };
    
    img.src = history[currentStep - 1];
    setCurrentStep(currentStep - 1);
  };

  const handleClear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, canvasWidth, canvasHeight);
    saveToHistory();
    
    if (isAnimationMode) {
      saveCurrentFrame();
    }
  };

  const addFrame = () => {
    saveCurrentFrame();
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, canvasWidth, canvasHeight);
    
    const newFrameData = canvas.toDataURL();
    setFrames([...frames, newFrameData]);
    setCurrentFrameIndex(frames.length);
    setHistory([]);
    setCurrentStep(-1);
    saveToHistory();
  };

  const deleteFrame = () => {
    if (frames.length <= 1) return;
    
    const newFrames = frames.filter((_, idx) => idx !== currentFrameIndex);
    setFrames(newFrames);
    
    const newIndex = Math.min(currentFrameIndex, newFrames.length - 1);
    setCurrentFrameIndex(newIndex);
  };

  const duplicateFrame = () => {
    saveCurrentFrame();
    const currentFrame = frames[currentFrameIndex];
    const newFrames = [...frames];
    newFrames.splice(currentFrameIndex + 1, 0, currentFrame);
    setFrames(newFrames);
    setCurrentFrameIndex(currentFrameIndex + 1);
  };

  const togglePlayback = () => {
    if (frames.length < 2) return;
    
    if (!isPlaying) {
      saveCurrentFrame();
    }
    
    setIsPlaying(!isPlaying);
  };

  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    if (e.touches && e.touches.length > 0) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY
      };
    } else {
      return {
        x: (e.clientX - rect.left) * scaleX,
        y: (e.clientY - rect.top) * scaleY
      };
    }
  };

  const startDrawing = (e) => {
    if (isPlaying) return;
    e.preventDefault();
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCoordinates(e);

    setIsDrawing(true);
    
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = isEraser ? backgroundColor : brushColor;
    ctx.lineWidth = isEraser ? brushSize * 3 : brushSize;
  };

  const draw = (e) => {
    if (!isDrawing || isPlaying) return;
    e.preventDefault();

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCoordinates(e);

    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    
    setIsDrawing(false);
    saveToHistory();
    
    if (isAnimationMode) {
      saveCurrentFrame();
    }
  };

  const handleSave = async () => {
    try {
      saveCurrentFrame();
      
      if (isAnimationMode && frames.length > 1) {
        // Create animated GIF using gif.js
        setIsPlaying(false);
        
        try {
          // Create GIF instance
          const gif = new GIF({
            workers: 2,
            quality: 10,
            width: canvasWidth,
            height: canvasHeight,
            workerScript: '/gif.worker.js' // Will be in public folder
          });
          
          // Load and add each frame
          const delay = Math.round(1000 / fps);
          
          for (const frameData of frames) {
            const img = new Image();
            await new Promise((resolve, reject) => {
              img.onload = resolve;
              img.onerror = reject;
              img.src = frameData;
            });
            
            gif.addFrame(img, { delay });
          }
          
          // Render GIF
          const gifBlob = await new Promise((resolve, reject) => {
            gif.on('finished', resolve);
            gif.on('error', reject);
            gif.render();
          });
          
          const file = new File(
            [gifBlob], 
            `animation-${Date.now()}.gif`, 
            { type: 'image/gif' }
          );
          
          onSave(file);
        } catch (error) {
          console.error('Error creating GIF:', error);
          alert('Failed to create animated GIF. Saving first frame as static image instead.');
          
          // Fallback to static image
          const canvas = canvasRef.current;
          const dataUrl = canvas.toDataURL('image/png');
          const blob = await fetch(dataUrl).then(r => r.blob());
          const file = new File([blob], `drawing-${Date.now()}.png`, { type: 'image/png' });
          onSave(file);
        }
      } else {
        // Save current canvas as static image
        const canvas = canvasRef.current;
        const dataUrl = canvas.toDataURL('image/png');
        const blob = await fetch(dataUrl).then(r => r.blob());
        const file = new File(
          [blob], 
          `drawing-${Date.now()}.png`, 
          { type: 'image/png' }
        );
        
        onSave(file);
      }
    } catch (error) {
      console.error('Error saving drawing:', error);
      alert('Failed to save drawing. Please try again.');
    }
  };

  const colors = [
    { name: 'Yellow', value: '#eab308' },
    { name: 'White', value: '#ffffff' },
    { name: 'Red', value: '#ef4444' },
    { name: 'Blue', value: '#3b82f6' },
    { name: 'Green', value: '#10b981' },
    { name: 'Orange', value: '#f97316' },
    { name: 'Purple', value: '#a855f7' },
    { name: 'Black', value: '#000000' },
  ];

  return createPortal(
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[9999] p-4 overflow-y-auto">
      <div className={`${dynastyTheme.components.card.base} p-6 rounded-lg max-w-5xl w-full my-4`}>
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <h3 className={`text-lg font-semibold ${dynastyTheme.classes.text.primary}`}>
              {isAnimationMode ? 'Create Animation' : 'Create Drawing'}
            </h3>
            <button
              onClick={() => {
                setIsAnimationMode(!isAnimationMode);
                if (!isAnimationMode) {
                  // Entering animation mode
                  saveCurrentFrame();
                }
              }}
              className={`${dynastyTheme.utils.getComponent('button', isAnimationMode ? 'primary' : 'ghost', 'sm')} flex items-center gap-2`}
              title="Toggle Animation Mode"
            >
              <Film className="w-4 h-4" />
              {isAnimationMode ? 'Animation Mode' : 'Static Mode'}
            </button>
          </div>
          <button
            onClick={onCancel}
            className={`p-1 rounded ${dynastyTheme.classes.text.neutralLight} hover:${dynastyTheme.classes.text.white} ${dynastyTheme.classes.transition}`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Animation Controls */}
        {isAnimationMode && (
          <div className={`mb-4 p-3 rounded ${dynastyTheme.classes.bg.neutralDark} border border-neutral-700`}>
            <div className="flex items-center justify-between flex-wrap gap-3">
              {/* Frame Navigation */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentFrameIndex(Math.max(0, currentFrameIndex - 1))}
                  disabled={currentFrameIndex === 0 || isPlaying}
                  className={`${dynastyTheme.utils.getComponent('button', 'ghost', 'sm')} ${
                    (currentFrameIndex === 0 || isPlaying) ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                
                <span className={`text-sm ${dynastyTheme.classes.text.white} px-2`}>
                  Frame {currentFrameIndex + 1} / {frames.length}
                </span>
                
                <button
                  onClick={() => setCurrentFrameIndex(Math.min(frames.length - 1, currentFrameIndex + 1))}
                  disabled={currentFrameIndex === frames.length - 1 || isPlaying}
                  className={`${dynastyTheme.utils.getComponent('button', 'ghost', 'sm')} ${
                    (currentFrameIndex === frames.length - 1 || isPlaying) ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>

              {/* Frame Actions */}
              <div className="flex items-center gap-2">
                <button
                  onClick={addFrame}
                  disabled={isPlaying}
                  className={`${dynastyTheme.utils.getComponent('button', 'ghost', 'sm')} flex items-center gap-1 ${
                    isPlaying ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                  title="Add new frame"
                >
                  <Plus className="w-4 h-4" />
                  Add Frame
                </button>
                
                <button
                  onClick={duplicateFrame}
                  disabled={isPlaying}
                  className={`${dynastyTheme.utils.getComponent('button', 'ghost', 'sm')} ${
                    isPlaying ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                  title="Duplicate current frame"
                >
                  Copy
                </button>
                
                <button
                  onClick={deleteFrame}
                  disabled={frames.length <= 1 || isPlaying}
                  className={`${dynastyTheme.utils.getComponent('button', 'ghost', 'sm')} ${
                    (frames.length <= 1 || isPlaying) ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                  title="Delete current frame"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>

              {/* Playback Controls */}
              <div className="flex items-center gap-3">
                <button
                  onClick={togglePlayback}
                  disabled={frames.length < 2}
                  className={`${dynastyTheme.utils.getComponent('button', isPlaying ? 'primary' : 'ghost', 'sm')} flex items-center gap-2 ${
                    frames.length < 2 ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                  {isPlaying ? 'Stop' : 'Play'}
                </button>
                
                <div className="flex items-center gap-2">
                  <span className={`text-xs ${dynastyTheme.classes.text.neutralLight}`}>FPS:</span>
                  <input
                    type="range"
                    min="1"
                    max="24"
                    value={fps}
                    onChange={(e) => setFps(Number(e.target.value))}
                    disabled={isPlaying}
                    className="w-20"
                  />
                  <span className={`text-xs ${dynastyTheme.classes.text.neutralLight} w-6`}>{fps}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Drawing Tools */}
        <div className="mb-4 space-y-3">
          {/* Pen/Eraser Toggle + Color Picker */}
          <div className="flex items-center gap-3 flex-wrap">
            {/* Eraser Toggle */}
            <button
              onClick={() => setIsEraser(!isEraser)}
              disabled={isPlaying}
              className={`${dynastyTheme.utils.getComponent('button', isEraser ? 'primary' : 'ghost', 'sm')} flex items-center gap-2 ${
                isPlaying ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              title="Toggle Eraser"
            >
              <Eraser className="w-4 h-4" />
              {isEraser ? 'Erasing' : 'Eraser'}
            </button>
            
            <div className="w-px h-6 bg-neutral-700"></div>
            
            {/* Color Palette */}
            <Palette className={`w-4 h-4 ${dynastyTheme.classes.text.neutralLight}`} />
            <span className={`text-sm ${dynastyTheme.classes.text.neutralLight}`}>Color:</span>
            <div className="flex gap-2">
              {colors.map((color) => (
                <button
                  key={color.value}
                  type="button"
                  onClick={() => {
                    setBrushColor(color.value);
                    setIsEraser(false);
                  }}
                  disabled={isPlaying}
                  className={`w-8 h-8 rounded border-2 ${dynastyTheme.classes.transition} ${
                    brushColor === color.value && !isEraser
                      ? 'border-yellow-500 scale-110' 
                      : 'border-neutral-600 hover:border-neutral-400'
                  } ${isPlaying ? 'opacity-50 cursor-not-allowed' : ''}`}
                  style={{ backgroundColor: color.value }}
                  title={color.name}
                />
              ))}
            </div>
          </div>

          {/* Brush Size */}
          <div className="flex items-center gap-3">
            <span className={`text-sm ${dynastyTheme.classes.text.neutralLight}`}>
              {isEraser ? 'Eraser' : 'Brush'} Size:
            </span>
            <input
              type="range"
              min="1"
              max="20"
              value={brushSize}
              onChange={(e) => setBrushSize(Number(e.target.value))}
              disabled={isPlaying}
              className="w-40"
            />
            <span className={`text-sm ${dynastyTheme.classes.text.neutralLight} w-12`}>
              {isEraser ? brushSize * 3 : brushSize}px
            </span>
          </div>
        </div>

        {/* Canvas */}
        <div className="border-2 border-neutral-700 rounded-lg overflow-hidden mb-4">
          <canvas
            ref={canvasRef}
            width={canvasWidth}
            height={canvasHeight}
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
            onTouchStart={startDrawing}
            onTouchMove={draw}
            onTouchEnd={stopDrawing}
            className={`w-full touch-none ${isPlaying ? 'cursor-not-allowed' : 'cursor-crosshair'}`}
            style={{ maxWidth: '100%', height: 'auto' }}
          />
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleUndo}
              disabled={currentStep <= 0 || isPlaying}
              className={`${dynastyTheme.utils.getComponent('button', 'ghost', 'sm')} flex items-center gap-2 ${
                (currentStep <= 0 || isPlaying) ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              title="Undo last stroke"
            >
              <Undo className="w-4 h-4" />
              Undo
            </button>
            
            <button
              type="button"
              onClick={handleClear}
              disabled={isPlaying}
              className={`${dynastyTheme.utils.getComponent('button', 'ghost', 'sm')} flex items-center gap-2 ${
                isPlaying ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              <RotateCcw className="w-4 h-4" />
              Clear Frame
            </button>
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onCancel}
              className={dynastyTheme.utils.getComponent('button', 'ghost', 'sm')}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={isPlaying}
              className={`${dynastyTheme.utils.getComponent('button', 'primary', 'sm')} ${
                isPlaying ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              Save {isAnimationMode && frames.length > 1 ? `Animation (${frames.length} frames)` : 'Drawing'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default DrawingCanvas;
